import { resolveShotComposition } from '../../../../../server/intelligence/production/shotContract.js';
import { stillReviewChanges, generationInputChanges } from '../../../../../server/intelligence/production/generationInputs.js';
import type { Project, ProjectJob } from '../../../lib/types';

export const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
export const rows = (value: unknown): Record<string, unknown>[] => Array.isArray(value) ? value.map(record) : [];
export const terminalJob = (status: unknown) => ['complete', 'completed', 'failed', 'cancelled', 'canceled'].includes(String(status));

export function videoMedia(job: ProjectJob) {
  const result = record(job.result), media = record(result.media);
  const mediaId = String(media.id || result.mediaId || '');
  const url = String(media.url || result.url || result.mediaUrl || (mediaId ? `/api/media/${encodeURIComponent(mediaId)}` : ''));
  const mime = String(media.mime || media.mimeType || result.mime || '');
  return { url, video: mime.startsWith('video/') || /\.(mp4|webm|mov)(\?|$)/i.test(url), image: mime.startsWith('image/') || /\.(png|jpe?g|webp)(\?|$)/i.test(url) };
}

export function jobMatchesShot(job: ProjectJob, shotId: string) {
  if (!shotId) return false;
  if (String(job.shotId || '') === shotId) return true;
  const payload = record(job.payload);
  const lineage = { ...record(payload.lineage), ...record(job.lineage) };
  return String(lineage.shotId || payload.shotId || '') === shotId;
}

export function selectedComposition(shot: Record<string, unknown>, lockedOnly = false): Record<string, unknown> | undefined {
  return resolveShotComposition(shot, lockedOnly ? 'video' : 'working');
}

export function shotCompositionUrl(shot: Record<string, unknown>): string {
  const preferred = selectedComposition(shot);
  if (preferred?.mediaId) return `/api/media/${encodeURIComponent(String(preferred.mediaId))}`;
  if (preferred?.url) return String(preferred.url);
  return '';
}

function shotFrameUrl(shot: Record<string, unknown>): string {
  const direct = String(
    shot.startFrameUrl || shot.image || shot.mediaUrl || shot.stillUrl || shot.thumbnailUrl || '',
  ).trim();
  if (direct) return direct;
  const mediaId = String(
    shot.startFrameMediaId || shot.mediaId || shot.stillMediaId || shot.heroStillId || '',
  ).trim();
  return mediaId ? `/api/media/${encodeURIComponent(mediaId)}` : '';
}

export function shotTakeUrl(shot: Record<string, unknown>, jobs: ProjectJob[] = []): string {
  const shotId = String(shot.id || '');
  const complete = jobs.filter((job) => {
    if (!jobMatchesShot(job, shotId) || job.type === 'agent') return false;
    if (!['complete', 'completed'].includes(String(job.status))) return false;
    const media = videoMedia(job);
    return media.image && Boolean(media.url);
  });
  if (!complete.length) return '';
  const latest = [...complete].sort((a, b) =>
    String(b.updatedAt || b.createdAt || b.id).localeCompare(String(a.updatedAt || a.createdAt || a.id)),
  )[0];
  return videoMedia(latest).url;
}

export function shotStillUrl(shot: Record<string, unknown>, jobs: ProjectJob[] = []): string {
  return shotCompositionUrl(shot) || shotTakeUrl(shot, jobs) || shotFrameUrl(shot);
}

export function productionScenes(project: Project, jobs: ProjectJob[]) {
  const unique = new Map<string, ProjectJob>();
  for (const job of jobs) {
    const previous = unique.get(job.id);
    unique.set(job.id, previous && terminalJob(previous.status) && !terminalJob(job.status) ? { ...job, ...previous } : { ...previous, ...job });
  }
  return rows(project.scenes).map(scene => {
    const id = String(scene.id);
    const shots = (project.shots || []).filter(shot => (shot.sceneId || shot.scene) === id);
    const attempts = [...unique.values()].filter(job => {
      const payload = record(job.payload), lineage = { ...record(payload.lineage), ...record(job.lineage) };
      const media = videoMedia(job);
      return lineage.sceneId === id && job.type !== 'agent' && !record(payload.projectContext).sceneDirect && !media.image && (media.video || Boolean(lineage.generationPlan));
    }).reverse();
    const active = attempts.filter(job => !terminalJob(job.status)).length;
    const locked = shots.filter(shot => shot.lockedCompositionId).length;
    const select = rows(scene.videoSelects).find(item => item.id === scene.selectedVideoId);
    const needsReview = generationInputChanges(project, select?.inputs).length > 0 || shots.some(shot => stillReviewChanges(project, shot).length > 0);
    const approved = Boolean(scene.selectedVideoId) && !needsReview;
    const status = needsReview ? 'Review changed inputs' : approved ? 'Scene select approved' : active ? 'Video in progress' : record(scene.direction).locked ? 'Direction locked' : locked ? `${locked}/${shots.length} compositions locked` : shots.length ? 'Shots drafted' : 'Ready to direct';
    return { id, title: String(scene.slug || scene.title || id), shots, attempts, active, approved, needsReview, status, duration: shots.reduce((sum, shot) => sum + (Number(shot.duration) || 0), 0) };
  });
}


export function shotProduceStatus(
  shot: Record<string, unknown>,
  jobs: ProjectJob[] = [],
  project?: Project,
): {
  label: string;
  tone: 'neutral' | 'info' | 'success' | 'danger';
  ready: boolean;
} {
  if (rows(shot.referenceChanges).length || (project && stillReviewChanges(project, shot).length)) return { label: 'Review references', tone: 'danger', ready: false };
  if (shot.lockedCompositionId) return { label: 'Composition locked', tone: 'success', ready: true };
  if (shotTakeUrl(shot, jobs)) return { label: 'Take ready', tone: 'info', ready: false };
  if (shotStillUrl(shot, jobs)) return { label: 'Still ready', tone: 'info', ready: false };
  const notes = String(shot.performanceNotes || shot.desc || shot.purpose || '').trim();
  if (notes) return { label: 'Directed', tone: 'info', ready: false };
  return { label: 'Needs direction', tone: 'neutral', ready: false };
}

export function sceneProducePhase(scene: {
  shots: Array<Record<string, unknown> | { lockedCompositionId?: unknown }>;
  attempts: Array<{ status?: unknown }>;
  needsReview?: boolean;
}): {
  phase: 'needs-direction' | 'needs-review' | 'ready' | 'queued' | 'review';
  label: string;
  tone: 'neutral' | 'info' | 'success' | 'danger';
} {
  const shots = scene.shots || [];
  const attempts = scene.attempts || [];
  if (scene.needsReview) return { phase: 'needs-review', label: 'Review inputs', tone: 'danger' };
  const active = attempts.some((job) => !terminalJob(job.status));
  const directed = shots.length > 0 && shots.every((shot) => {
    const item = shot as Record<string, unknown>;
    return Boolean(String(item.performanceNotes || item.desc || item.purpose || '').trim());
  });
  if (!directed) {
    return { phase: 'needs-direction', label: 'Needs direction', tone: 'neutral' };
  }
  if (active) {
    return { phase: 'queued', label: 'Queued', tone: 'info' };
  }
  if (attempts.some((job) => ['complete', 'completed'].includes(String(job.status)))) {
    return { phase: 'review', label: 'Takes ready', tone: 'success' };
  }
  return { phase: 'ready', label: 'Ready', tone: 'info' };
}
